import { useEffect, useState } from 'react'
import { Keyboard, X } from 'lucide-react'

const STORAGE_KEY = 'ctod.kbdHintsDismissed'

/**
 * Small floating "?" badge bottom-right that explains the keyboard shortcuts.
 * Click ? to expand; click X to dismiss permanently.
 */
export default function KeyboardHints() {
  const [open, setOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    try { setDismissed(localStorage.getItem(STORAGE_KEY) === '1') } catch {}
  }, [])

  if (dismissed) return null

  return (
    <div className="fixed bottom-3 right-3 z-40">
      {open ? (
        <div className="card p-3 text-[11px] text-text-secondary border border-border-light shadow-lg max-w-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-text-primary flex items-center gap-1.5">
              <Keyboard size={12} /> Keyboard shortcuts
            </span>
            <button type="button" onClick={() => setOpen(false)} className="text-text-muted hover:text-text-primary" title="Collapse">
              <X size={11} />
            </button>
          </div>
          <table className="w-full">
            <tbody className="space-y-1">
              <Row k="⌘K"  desc="Command palette (search anything)" />
              <Row k="/"   desc="Focus the in-page search box" />
              <Row k="j / k" desc="Move down / up in any list" />
              <Row k="o / ↵" desc="Open the focused item" />
              <Row k="g g" desc="Jump to top of list" />
              <Row k="⇧G"  desc="Jump to bottom of list" />
              <Row k="esc" desc="Clear selection / blur search" />
            </tbody>
          </table>
          <button type="button" onClick={() => {
            try { localStorage.setItem(STORAGE_KEY, '1') } catch {}
            setDismissed(true)
          }}
            className="mt-2 text-[10px] text-text-muted hover:text-text-primary">
            Got it — don't show again
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)}
          className="rounded-full p-2 bg-surface-card border border-border hover:border-border-light text-text-muted hover:text-text-primary shadow"
          title="Keyboard shortcuts"
          aria-label="Show keyboard shortcuts">
          <Keyboard size={14} />
        </button>
      )}
    </div>
  )
}

function Row({ k, desc }: { k: string; desc: string }) {
  return (
    <tr>
      <td className="pr-3 py-0.5 align-top">
        <kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-border text-text-primary whitespace-nowrap">{k}</kbd>
      </td>
      <td className="py-0.5 text-text-secondary">{desc}</td>
    </tr>
  )
}
